import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map, take, filter } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';

export const authGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return authService.authState$.pipe(
    // Wait for auth initialization to complete (loading: false)
    filter(authState => !authState.loading),
    take(1),
    map(authState => {
      if (authState.isAuthenticated) {
        return true;
      } else {
        // Redirect to login page with return url
        router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
        return false;
      }
    })
  );
};

export const roleGuard = (requiredRole: string): CanActivateFn => {
  return (route, state) => {
    const authService = inject(AuthService);
    const router = inject(Router);

    return authService.authState$.pipe(
      // Wait for auth initialization to complete (loading: false)
      filter(authState => !authState.loading),
      take(1),
      map(authState => {
        if (!authState.isAuthenticated) {
          router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
          return false;
        }

        const userRole = authState.user?.role;
        if (userRole === requiredRole || userRole === 'super_admin') {
          return true;
        } else {
          // Redirect to unauthorized page or home
          router.navigate(['/unauthorized']);
          return false;
        }
      })
    );
  };
};

export const rolesGuard = (allowedRoles: string[]): CanActivateFn => {
  return (route, state) => {
    const authService = inject(AuthService);
    const router = inject(Router);

    return authService.authState$.pipe(
      // Wait for auth initialization to complete (loading: false)
      filter(authState => !authState.loading),
      take(1),
      map(authState => {
        if (!authState.isAuthenticated) {
          router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
          return false;
        }

        const userRole = authState.user?.role;
        if (allowedRoles.includes(userRole || '') || userRole === 'super_admin') {
          return true;
        } else {
          // Redirect to unauthorized page or home
          router.navigate(['/unauthorized']);
          return false;
        }
      })
    );
  };
};

export const permissionGuard = (requiredPermission: string): CanActivateFn => {
  return (route, state) => {
    const authService = inject(AuthService);
    const router = inject(Router);

    return authService.authState$.pipe(
      // Wait for auth initialization to complete (loading: false)
      filter(authState => !authState.loading),
      take(1),
      map(authState => {
        if (!authState.isAuthenticated) {
          router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
          return false;
        }

        const hasPermission = authService.hasPermission(requiredPermission);
        if (hasPermission) {
          return true;
        } else {
          // Redirect to unauthorized page or home
          router.navigate(['/unauthorized']);
          return false;
        }
      })
    );
  };
};

/**
 * Guard that allows admins/super_admins to access any auctioneer profile,
 * but only allows auctioneers to access their own profile
 */
export const auctioneerProfileGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return authService.authState$.pipe(
    filter(authState => !authState.loading),
    take(1),
    map(authState => {
      if (!authState.isAuthenticated) {
        router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
        return false;
      }

      const userRole = authState.user?.role;
      const profileId = route.paramMap.get('id'); // Get the :id from the route

      // Super admins and admins can access any profile
      if (userRole === 'super_admin' || userRole === 'admin') {
        return true;
      }

      // Auctioneers can only access their own profile
      if (userRole === 'auctioneer') {
        const auctioneerId = authState.user?.auctioneerId;

        if (auctioneerId && profileId === auctioneerId) {
          return true;
        } else {
          router.navigate(['/unauthorized']);
          return false;
        }
      }

      // All other roles are not allowed
      router.navigate(['/unauthorized']);
      return false;
    })
  );
};
